import { useState, useMemo, useEffect } from "react";
import { ModelTypeFilter } from "./ModelTypeFilter";
import { FuelLegend } from "./FuelLegend";
import type { CarBrand } from "../data/carBrands";
import { findModelImage, getHighResImage, DEFAULT_CAR_IMAGE, isSimilarModelImage } from "../utils/imageMatching";
import { getBrandLogo } from "../utils/brandLogos";

interface BrandDetailProps {
  brand: CarBrand;
}

export const BrandDetail = ({ brand }: BrandDetailProps) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [modelImageErrors, setModelImageErrors] = useState<Set<string>>(new Set());

  // Reset filter when brand changes
  useEffect(() => {
    setSelectedType(null);
    setImageError(false);
    setModelImageErrors(new Set());
  }, [brand.id]);

  // Brands available on Carwow
  const carwowAvailableBrands = [
    "Alpine", "Audi", "BMW", "BYD", "Citroën", "Cupra", "Dacia", "Dongfeng",
    "EBRO", "Fiat", "Ford", "Honda", "Hyundai", "JAECOO", "Jeep", "KIA",
    "Leapmotor", "Lexus", "Mazda", "Mercedes", "MG", "MINI", "Nissan",
    "OMODA", "Opel", "Peugeot", "Polestar", "Renault", "SEAT", "Skoda",
    "smart", "Suzuki", "Tesla", "Toyota", "Volkswagen", "Volvo", "XPENG"
  ];

  const isReviewAvailable = carwowAvailableBrands.some(
    availableBrand => availableBrand.toLowerCase() === brand.name.toLowerCase()
  );

  const generateReviewUrl = (brandName: string, modelName: string): string => {
    if (isReviewAvailable) {
      // Use Google search with site:carwow.es for brands available on Carwow
      const searchQuery = `${brandName} ${modelName} site:carwow.es`;
      return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
    } else {
      // Use Google search with "review" for brands not on Carwow
      const searchQuery = `${brandName} ${modelName} "review"`;
      return `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleImageClick = (brandName: string, modelName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const query = `${brandName} ${modelName}`;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}&tbm=isch`, '_blank');
  };

  const handleReviewClick = (brandName: string, modelName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const reviewUrl = generateReviewUrl(brandName, modelName);
    window.open(reviewUrl, '_blank');
  };

  // Group fuel types into main categories
  const getFuelCategory = (fuel: string): string => {
    const fuelLower = fuel.toLowerCase();
    
    if (fuelLower.includes('elèctric') && !fuelLower.includes('híbrid') && !fuelLower.includes('phev')) {
      return 'Elèctric';
    }
    if (fuelLower.includes('phev') || fuelLower.includes('híbrid') || fuelLower.includes('mhev') || fuelLower.includes('hev')) {
      return 'Híbrid';
    }
    if (fuelLower.includes('gasolina') && fuelLower.includes('dièsel')) {
      return 'Gasolina / Dièsel';
    }
    if (fuelLower.includes('gasolina')) {
      return 'Gasolina';
    }
    if (fuelLower.includes('dièsel') || fuelLower.includes('diesel')) {
      return 'Dièsel';
    }
    
    return fuel; // Return original if no match
  };

  const availableTypes = useMemo(() => {
    const categories = new Set<string>();
    brand.models.forEach((model) => {
      if (model.fuel) {
        categories.add(getFuelCategory(model.fuel));
      }
    });
    
    // Custom sort order
    const fuelOrder = ['Elèctric', 'Híbrid', 'Gasolina', 'Dièsel', 'Gasolina / Dièsel'];
    
    return Array.from(categories).sort((a, b) => {
      const indexA = fuelOrder.indexOf(a);
      const indexB = fuelOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [brand.models]);

  const filteredModels = useMemo(() => {
    if (!selectedType) {
      return brand.models;
    }
    return brand.models.filter((model) => {
      // Only show models that have a fuel type AND its category matches the selected type
      return model.fuel && getFuelCategory(model.fuel) === selectedType;
    });
  }, [brand.models, selectedType]);

  const modelsByType = useMemo(() => {
    const grouped: Record<string, number> = {};
    brand.models.forEach((model) => {
      const type = model.fuel ? getFuelCategory(model.fuel) : "Altres";
      grouped[type] = (grouped[type] || 0) + 1;
    });
    return grouped;
  }, [brand.models]);

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center mb-3">
          <div className="w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
            {!imageError ? (
              <div className="w-full h-full rounded-2xl bg-white border border-transparent dark:border-white/15 flex items-center justify-center p-2.5">
                <img
                  src={getBrandLogo(brand.id)}
                  alt={`${brand.name} logo`}
                  className="w-full h-full object-contain"
                  onError={() => setImageError(true)}
                />
              </div>
            ) : (
              <span className="text-6xl sm:text-7xl">{brand.logo}</span>
            )}
          </div>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground tracking-tight">
          {brand.name}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground font-light mb-4">
          {brand.description}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 text-sm">
          <div className="px-3 py-1.5 bg-foreground text-background rounded-full font-medium">
            {brand.models.length} models actuals
          </div>
          {brand.discontinuedModels && brand.discontinuedModels.length > 0 && (
            <a
              href="#discontinued-models"
              className="px-3 py-1.5 bg-red-600 text-white rounded-full font-medium hover:bg-red-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('discontinued-models')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {brand.discontinuedModels.length} models descatalogats
            </a>
          )}
          {Object.entries(modelsByType)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([type, count]) => (
              <div
                key={type}
                className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded-full font-light"
              >
                {count} {type}
              </div>
            ))}
        </div>
      </div>

      <div className="border-t border-border pt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
            Models actuals
          </h2>
          <span className="text-sm text-muted-foreground font-light">
            {filteredModels.length}{" "}
            {selectedType ? `de ${brand.models.length}` : ""}
          </span>
        </div>

        <FuelLegend />
        
        <ModelTypeFilter
          selectedType={selectedType}
          types={availableTypes}
          onTypeChange={setSelectedType}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredModels.map((model) => {
            const modelImage = findModelImage(brand.id, model.id, false);
            const highResImage = getHighResImage(modelImage, 500);
            
            return (
              <div
                key={model.id}
                data-fuel={model.fuel || 'none'}
                className="relative bg-card border border-border rounded-2xl overflow-hidden hover:border-foreground hover:shadow-lg transition-all duration-300"
              >
                {/* Model Image */}
                <div className="relative w-full h-48 bg-gradient-to-br from-secondary to-muted flex items-center justify-center p-4">
                  {isSimilarModelImage(brand.id, model.id) && (
                    <span className="absolute left-3 top-3 z-10 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
                      Model similar
                    </span>
                  )}
                  <img
                    src={modelImageErrors.has(model.id) ? DEFAULT_CAR_IMAGE : highResImage}
                    alt={model.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    onError={() => setModelImageErrors(prev => new Set(prev).add(model.id))}
                  />
                </div>

                <div className="p-6">
                  {/* Fuel badge - top right */}
                  {model.fuel && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center justify-center px-3 py-1.5 bg-card/90 backdrop-blur-sm text-card-foreground rounded-full text-xs font-semibold shadow-sm">
                        {model.fuel}
                      </span>
                    </div>
                  )}

                  {/* Header with name */}
                  <div className="mb-4 pb-4 border-b border-border">
                    <h3 className="text-lg font-bold text-foreground">
                      {model.name}
                    </h3>
                  </div>

                  {/* Specs Grid */}
                  <div className="space-y-3 mb-4">
                    {model.power && (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm text-muted-foreground flex items-center gap-2 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                          </svg>
                          Potència
                        </span>
                        <span className="text-sm font-semibold text-foreground text-right break-words">{model.power} CV</span>
                      </div>
                    )}
                    
                    {model.trunk && (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm text-muted-foreground flex items-center gap-2 flex-shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect width="20" height="14" x="2" y="7" rx="2" ry="2"/>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                          </svg>
                          Maleter
                        </span>
                        <span className="text-sm font-semibold text-foreground text-right">{model.trunk} L</span>
                      </div>
                    )}

                    {(model.length || model.width || model.height) && (
                      <div className="pt-2 border-t border-border">
                        <div className="flex items-center gap-2 mb-2">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                          </svg>
                          <span className="text-xs text-muted-foreground">Dimensions</span>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono bg-secondary px-2 py-1.5 rounded">
                          {model.length && `${model.length}`}
                          {model.width && ` × ${model.width}`}
                          {model.height && ` × ${model.height}`} mm
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  {model.price && (
                    <div className="pt-4 mt-4 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Des de</span>
                        <span className="text-xl font-bold text-foreground">
                          {model.price.includes('€') ? model.price : `${model.price}€`}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Buttons - visible on all devices */}
                  <div className="mt-4 pt-3 border-t border-border flex gap-2">
                    <button
                      onClick={(e) => handleImageClick(brand.name, model.name, e)}
                      className="flex-1 px-4 py-2.5 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                      aria-label={`Veure imatges de ${model.name}`}
                    >
                      Imatges
                    </button>
                    <button
                      onClick={(e) => handleReviewClick(brand.name, model.name, e)}
                      className="flex-1 px-4 py-2.5 bg-card text-card-foreground border border-border rounded-xl font-semibold text-sm hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                      aria-label={`Veure ressenya de ${model.name}`}
                    >
                      Ressenya
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Discontinued Models Section */}
      {brand.discontinuedModels && brand.discontinuedModels.length > 0 && (
        <div id="discontinued-models" className="border-t border-border pt-8 mt-8 scroll-mt-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-red-500">
              Models descatalogats
            </h2>
            <span className="text-sm text-red-500 font-light">
              {brand.discontinuedModels.length} models
            </span>
          </div>

          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <p className="text-sm text-red-500 dark:text-red-400">
              Aquests models ja no estan en producció. La informació mostrada pot estar incompleta.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {brand.discontinuedModels.map((model) => {
              const modelImage = findModelImage(brand.id, model.id, true);
              const highResImage = getHighResImage(modelImage, 500);
              
              return (
                <div
                  key={model.id}
                  data-fuel={model.fuel || 'none'}
                  className="relative bg-card border border-border rounded-2xl overflow-hidden hover:border-foreground hover:shadow-lg transition-all duration-300 opacity-75 hover:opacity-100"
                >
                  {/* Model Image */}
                  <div className="relative w-full h-48 bg-gradient-to-br from-red-500/10 to-red-500/20 flex items-center justify-center p-4">
                    {isSimilarModelImage(brand.id, model.id) && (
                      <span className="absolute left-3 top-3 z-10 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
                        Model similar
                      </span>
                    )}
                    <img
                      src={modelImageErrors.has(model.id) ? DEFAULT_CAR_IMAGE : highResImage}
                      alt={model.name}
                      className="w-full h-full object-contain grayscale"
                      loading="lazy"
                      onError={() => setModelImageErrors(prev => new Set(prev).add(model.id))}
                    />
                  </div>

                  <div className="p-6">
                    {/* Fuel badge - top right */}
                    {model.fuel && (
                      <div className="absolute top-4 right-4">
                        <span className="inline-flex items-center justify-center px-3 py-1.5 bg-card/90 backdrop-blur-sm text-card-foreground rounded-full text-xs font-semibold shadow-sm">
                          {model.fuel}
                        </span>
                      </div>
                    )}

                    {/* Header with name */}
                    <div className="mb-4 pb-4 border-b border-border">
                      <h3 className="text-lg font-bold text-foreground">
                        {model.name}
                      </h3>
                    </div>

                    {/* Specs Grid */}
                    <div className="space-y-3 mb-4">
                      {model.generations && (
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm text-muted-foreground flex items-center gap-2 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                              <path d="M3 3v5h5"/>
                              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                              <path d="M16 16h5v5"/>
                            </svg>
                            Generacions
                          </span>
                          <span className="text-sm font-semibold text-foreground text-right break-words">{model.generations}</span>
                        </div>
                      )}
                      
                      {model.years && (
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-sm text-muted-foreground flex items-center gap-2 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8" y1="2" x2="8" y2="6"/>
                              <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            Anys
                          </span>
                          <span className="text-sm font-semibold text-foreground text-right">{model.years}</span>
                        </div>
                      )}
                    </div>

                    {/* Buttons - visible on all devices */}
                    <div className="mt-4 pt-3 border-t border-border flex gap-2">
                      <button
                        onClick={(e) => handleImageClick(brand.name, model.name, e)}
                        className="flex-1 px-4 py-2.5 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                        aria-label={`Veure imatges de ${model.name}`}
                      >
                        Imatges
                      </button>
                      <button
                        onClick={(e) => handleReviewClick(brand.name, model.name, e)}
                        className="flex-1 px-4 py-2.5 bg-card text-card-foreground border border-border rounded-xl font-semibold text-sm hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                        aria-label={`Veure ressenya de ${model.name}`}
                      >
                        Ressenya
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

